import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaCheck, FaCrown, FaRocket, FaGem, FaStar, FaMedal } from 'react-icons/fa';
import axios from 'axios';
import { config } from '../config';

import * as FaIcons from 'react-icons/fa';
import * as MdIcons from 'react-icons/md';
import * as AiIcons from 'react-icons/ai';
import * as BsIcons from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';

const apiUrl = config.apiUrl;

const HomeMemberships = () => {
  const [memberships, setMemberships] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${apiUrl}/memberships`);
        setMemberships(response.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load membership plans. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [apiUrl]);

  const membershipIcons = [<FaMedal className="text-amber-500" />, <FaCrown className="text-yellow-500" />, <FaRocket className="text-purple-500" />];
  const membershipColors = ["from-blue-500 to-blue-600", "from-amber-500 to-amber-600", "from-purple-500 to-purple-600"];

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const getIconComponent = (iconName) => {
      const libraries = { ...FaIcons, ...MdIcons, ...AiIcons, ...BsIcons };
      const Icon = libraries[iconName];
      return Icon ? <Icon /> : <span>✅</span>;
    };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-16 px-4 sm:px-6 lg:px-8">
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        {/* Hero Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full mb-6 shadow-lg"
          >
            <FaStar className="mr-2" />
            <span className="font-medium">EXCLUSIVE PLANS</span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            Choose Your Perfect Plan
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Join thousands of satisfied members. Cancel or upgrade anytime.
          </motion.p>
        </div>

        {/* Loading State */}
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-center items-center h-64"
          >
            <div className="animate-pulse flex space-x-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-200 rounded-lg w-64 h-80"></div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Error State */}
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-red-50 border-l-4 border-red-500 p-4 mb-8 rounded-lg shadow"
          >
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Membership Cards */}
        {!loading && memberships.length > 0 && (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto"
          >
            {memberships.map((membership, index) => (
              <motion.div
                key={membership.id}
                variants={item}
                whileHover={{ y: -10 }}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                className={`relative rounded-xl shadow-xl overflow-hidden transition-all duration-300 ${hoveredCard === index ? 'shadow-2xl' : ''}`}
              >
                {/* Recommended Badge */}
                {index === 1 && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-xs font-bold px-4 py-1 transform translate-x-2 translate-y-2 rotate-12 rounded-full shadow-md z-10">
                    MOST POPULAR
                  </div>
                )}

                {/* Card Header */}
                <div className={`bg-gradient-to-r ${membershipColors[index]} p-6 text-white`}>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl">
                      {membershipIcons[index]}
                    </div>
                    <div className="text-right">
                      <span className="text-sm opacity-80">STARTING AT</span>
                      <div className="text-3xl font-bold">{membership.discount_price} Tk</div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold mt-4">{membership.membership_type}</h3>
                </div>

                {/* Dynamic Benefits */}
                {membership.benefits && membership.benefits.length > 0 && (
                  <div className="mt-6 mb-4 mx-4">
                    {membership.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center mb-2">
                        <span className="mr-2">{getIconComponent(benefit.icon)}</span>
                        <span className="text-gray-700">{benefit.benefit_name}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Card Body */}
                <div className="bg-white p-6">
                  <div className="mb-6">
                    <div className="text-gray-500 line-through mb-1">Regular Price: {membership.selling_price} Tk</div>
                    <div className="text-green-600 font-bold">You save: {membership.selling_price - membership.discount_price} Tk!</div>
                  </div>

                  {/* <ul className="space-y-3 mb-8">
                    { membership.membership_description &&
                    (membership.membership_description.split('<br>').map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <FaCheck className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    )))
                    }
                  </ul> */}

                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => navigate(`/purchase/membership/${membership.id}`)}
                    className={`w-full py-3 px-6 rounded-lg font-bold text-white transition-all ${index === 1 ? 'bg-gradient-to-r from-amber-500 to-amber-600 shadow-lg' : 'bg-gradient-to-r from-blue-500 to-blue-600'}`}
                  >
                    Get Started
                  </motion.button>
                </div>

                {/* Floating particles for hover effect */}
                {hoveredCard === index && (
                  <>
                    <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
                      {[...Array(10)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute rounded-full bg-white opacity-20"
                          style={{
                            width: Math.random() * 10 + 5 + 'px',
                            height: Math.random() * 10 + 5 + 'px',
                            top: Math.random() * 100 + '%',
                            left: Math.random() * 100 + '%',
                          }}
                          animate={{
                            y: [0, (Math.random() - 0.5) * 30],
                            x: [0, (Math.random() - 0.5) * 20],
                            opacity: [0.1, 0.3, 0.1]
                          }}
                          transition={{
                            duration: Math.random() * 5 + 3,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* No Memberships Available */}
        {!loading && memberships.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="bg-white p-8 rounded-xl shadow-lg inline-block">
              <FaGem className="text-gray-400 text-5xl mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-800 mb-2">No Plans Available</h3>
              <p className="text-gray-600 max-w-md mx-auto">
                We're currently updating our membership plans. Please check back soon for exciting new options!
              </p>
            </div>
          </motion.div>
        )}

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto text-center"
        >
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="text-blue-600 text-2xl mb-2">✓</div>
            <div className="font-medium text-gray-700">No Hidden Fees</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="text-blue-600 text-2xl mb-2">🔄</div>
            <div className="font-medium text-gray-700">Flexible Cancellation</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="text-blue-600 text-2xl mb-2">🔒</div>
            <div className="font-medium text-gray-700">Secure Payment</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="text-blue-600 text-2xl mb-2">24/7</div>
            <div className="font-medium text-gray-700">Customer Support</div>
          </div>
        </motion.div>

        {/* FAQ CTA
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-20 text-center"
        >
          <h3 className="text-2xl font-bold text-gray-800 mb-4">Need help choosing?</h3>
          <button className="bg-white text-blue-600 font-bold py-3 px-8 rounded-full border-2 border-blue-600 hover:bg-blue-50 transition-colors duration-300 shadow-sm">
            Compare Plans
          </button>
        </motion.div> */}
      </motion.div>
    </div>
  );
};

export default HomeMemberships;